import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Search, LayoutGrid, Dices, Wrench, SearchX } from "lucide-react";
import { Loading } from "@/components/ui/loading";
import { PositionsTable } from "@/components/admin/pricing/components/PositionsTable";
import { FilterTabs } from "@/components/brand/FilterTabs";
import { EmptyState } from "@/components/brand/EmptyState";
import { positionService } from "@/services/positionService";
import { useDebounce } from "@/hooks/useDebounce";

/**
 * `undefined` rather than a sentinel: the server reads an absent parameter as
 * "either", and the service sends any value it is given.
 */
const TYPES: { label: string; icon: React.ElementType; isTable?: boolean }[] = [
  { label: "All", icon: LayoutGrid },
  { label: "Table games", icon: Dices, isTable: true },
  { label: "Other services", icon: Wrench, isTable: false },
];

export default function ManagePricing() {
  const [typeIndex, setTypeIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  // 500ms, as every other admin list here.
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const isTable = TYPES[typeIndex].isTable;
  const trimmed = debouncedSearchTerm.trim();

  // Only the keys actually set: the service turns any defined value into a
  // query parameter, so `isTable: undefined` would send "isTable=undefined".
  const filter = {
    ...(isTable !== undefined && { isTable }),
    ...(trimmed && { searchTerm: trimmed }),
  };

  const { data: tableTypes, isLoading } = useQuery({
    queryKey: ["positions", isTable, trimmed],
    queryFn: () => positionService.getPositions(filter),
    // Without this the table empties on every keystroke and the button below
    // it jumps up under the cursor.
    placeholderData: keepPreviousData,
  });

  const filtered = Boolean(isTable !== undefined || trimmed);

  return (
    <div className="container mx-auto p-4 md:p-6">
      <h1 className="text-3xl font-bold text-foreground mb-8">
        Manage Pricing
      </h1>

      <div className="mb-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <FilterTabs
          tabs={TYPES}
          active={typeIndex}
          onChange={setTypeIndex}
          className="md:max-w-sm"
        />
        <div className="relative md:w-72">
          <Label htmlFor="pricing-search" className="sr-only">
            Search table types
          </Label>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="pricing-search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search name or description"
            className="pl-9"
          />
        </div>
      </div>

      {isLoading ? (
        <Loading />
      ) : (tableTypes || []).length === 0 && filtered ? (
        // Distinct from having no table types at all, which here would mean
        // something has gone badly wrong.
        <div className="mb-4 rounded-md border">
          <EmptyState
            icon={SearchX}
            title="No table types match these filters"
            description="Try a different search, or widen the type filter."
            action={
              <Button
                variant="outline"
                onClick={() => {
                  setTypeIndex(0);
                  setSearchTerm("");
                }}
              >
                Clear filters
              </Button>
            }
          />
        </div>
      ) : (
        <div className="rounded-md border mb-4 overflow-x-auto">
          <PositionsTable tableTypes={tableTypes || []} />
        </div>
      )}

      <Link to={`/admin/pricing/new`}>
        <Button>Add New Table Type</Button>
      </Link>
    </div>
  );
}
